"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Trophy, Users, Clock, Target } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const INFO_CARD_CLASSES = cn(
  "group border-muted/20 bg-transparent shadow-sm rounded-2xl overflow-hidden h-full",
  "hover:shadow-md transition-all duration-300"
);

const HERO_TITLE_CLASSES = cn(
  "text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter mb-6 uppercase text-foreground drop-shadow-sm"
);

const HERO_BADGE_CLASSES = cn(
  "mb-4 bg-muted/50 text-muted-foreground border-muted/20 text-[10px] font-black uppercase tracking-widest px-4 py-1 rounded-full shadow-xs"
);

export function SovenskoHero() {
  const HERO_TITLE = "Program turnaja";
  const HERO_SUBTITLE = "ZOH 2026";
  const LOCATION_TEXT = "Miláno, Taliansko";
  const HERO_DESCRIPTION =
    "Hokejový turnaj mužov na Zimných olympijských hrách 2026 v Miláne. Sledujte zábery 12 najlepších svetových tímov v boji o olympijské medaily.";
  const DURATION_LABEL = "Trvanie turnaja";
  const LOCATION_LABEL = "Hostiteľské mesto";
  const TEAMS_LABEL = "Počet tímov";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="space-y-12"
    >
      {/* Hero Section */}
      <section className="text-center relative overflow-hidden py-16 md:py-24 border-b border-muted/20 bg-muted/5">
        <div className="container mx-auto relative z-10 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-10"
          >
            <div className="relative p-6 bg-muted/10 border border-muted/20 rounded-full shadow-inner">
              <Trophy className="h-12 w-12 text-primary drop-shadow-sm" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Badge variant="outline" className={HERO_BADGE_CLASSES}>
              <Calendar className="h-3 w-3 mr-2 text-primary/60" />
              11. - 22. februára 2026
            </Badge>

            <h1 className={HERO_TITLE_CLASSES}>
              {HERO_TITLE}
              <br />
              <span className="text-primary">{HERO_SUBTITLE}</span>
            </h1>

            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="p-1 bg-muted/50 rounded-full">
                <MapPin className="h-4 w-4 text-primary/60" />
              </div>
              <p className="text-lg md:text-xl text-muted-foreground/80 font-bold uppercase tracking-widest">
                {LOCATION_TEXT}
              </p>
            </div>

            <p className="text-lg md:text-xl text-muted-foreground/70 max-w-3xl mx-auto leading-relaxed font-medium">
              {HERO_DESCRIPTION}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap items-center justify-center gap-4 mt-12 sm:gap-6"
          >
            <Badge
              variant="secondary"
              className="px-5 py-2 text-[10px] font-black uppercase tracking-widest bg-muted/50 border border-muted/20 rounded-full shadow-sm"
            >
              <Users className="h-3.5 w-3.5 mr-2 text-primary/60" />
              12 krajín
            </Badge>
            <Badge
              variant="secondary"
              className="px-5 py-2 text-[10px] font-black uppercase tracking-widest bg-muted/50 border border-muted/20 rounded-full shadow-sm"
            >
              <Target className="h-3.5 w-3.5 mr-2 text-primary/60" />3 skupiny
            </Badge>
            <Badge
              variant="secondary"
              className="px-5 py-2 text-[10px] font-black uppercase tracking-widest bg-muted/50 border border-muted/20 rounded-full shadow-sm"
            >
              <Clock className="h-3.5 w-3.5 mr-2 text-primary/60" />
              12 dní
            </Badge>
          </motion.div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className={INFO_CARD_CLASSES}>
            <CardContent className="pt-10 pb-8 text-center relative flex flex-col items-center">
              <div className="mb-6 p-4 bg-status-info/10 rounded-2xl text-status-info shadow-inner">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="font-black text-xs uppercase tracking-[0.2em] text-muted-foreground/60 mb-3">
                {DURATION_LABEL}
              </h3>
              <p className="text-2xl font-black text-foreground tracking-tighter uppercase mb-1">
                11. - 22. februára
              </p>
              <p className="text-[10px] font-black text-muted-foreground/40 uppercase tracking-widest">
                2026
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card className={INFO_CARD_CLASSES}>
            <CardContent className="pt-10 pb-8 text-center relative flex flex-col items-center">
              <div className="mb-6 p-4 bg-status-error/10 rounded-2xl text-status-error shadow-inner">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="font-black text-xs uppercase tracking-[0.2em] text-muted-foreground/60 mb-3">
                {LOCATION_LABEL}
              </h3>
              <p className="text-2xl font-black text-foreground tracking-tighter uppercase mb-1">
                Milano
              </p>
              <p className="text-[10px] font-black text-muted-foreground/40 uppercase tracking-widest">
                Taliansko
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card className={INFO_CARD_CLASSES}>
            <CardContent className="pt-10 pb-8 text-center relative flex flex-col items-center">
              <div className="mb-6 p-4 bg-status-success/10 rounded-2xl text-status-success shadow-inner">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="font-black text-xs uppercase tracking-[0.2em] text-muted-foreground/60 mb-3">
                {TEAMS_LABEL}
              </h3>
              <p className="text-2xl font-black text-foreground tracking-tighter uppercase mb-1">
                12 krajín
              </p>
              <p className="text-[10px] font-black text-muted-foreground/40 uppercase tracking-widest">
                3 skupiny
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </section>
    </motion.div>
  );
}
